import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { SubSink } from 'subsink';
import { SessionPaymentTransaction, TransactionStatus } from '../../models';
import { TitleCasePipe } from '@angular/common';

type ColumnField =
  | 'confirmationNumber'
  | 'transactionNumber'
  | 'buyerAccount'
  | 'discountApplied'
  | 'subtotalAmount'
  | 'creditMemoAmount'
  | 'creditCardProcessingFee'
  | 'paymentAmount'
  | 'status';

interface Column {
  field: ColumnField;
  header: string;
  visible: boolean;
  sortable: boolean;
  wrap?: boolean;
  currency?: boolean;
  frozen?: boolean;
  alignFrozen?: 'right';
  width?: string;
}

@Component({
  selector: 'payment-transactions-table',
  templateUrl: './payment-transactions-table.component.html',
  styleUrls: ['./payment-transactions-table.component.scss'],
  providers: [TitleCasePipe],
})
export class PaymentTransactionsTableComponent implements OnInit, OnDestroy {
  private readonly subSink = new SubSink();
  public readonly minPageHeight = 768;
  public readonly headerHeight = 33;
  public readonly bordersHeight = 3;
  public readonly rowHeight = 41;
  public readonly minRowCount = 4;
  public readonly noScrollHeight =
    this.headerHeight + this.bordersHeight + this.rowHeight * this.minRowCount;

  @Input() isSurchargeFeeVisible: boolean;
  @Input() excludedColumns: ColumnField[];
  @Input() transactions: SessionPaymentTransaction[];

  columnMap: Record<ColumnField, Column>;
  columns: Column[];
  scrollHeight = `${this.noScrollHeight}px`;
  transactionStatus = TransactionStatus;

  constructor(private changeDetector: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.initializeColumns();
    this.setVisibleColumns();

    if (this.transactions.length > this.minRowCount) {
      this.calculateScrollHeight();
    }
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  private initializeColumns() {
    this.columnMap = {
      confirmationNumber: {
        field: 'confirmationNumber',
        header: 'ARC_PAYMENT_CONFIRMATION_NUMBER',
        visible: true,
        sortable: true,
        wrap: true,
        width: '150px',
      },
      transactionNumber: {
        field: 'transactionNumber',
        header: 'ARC_PAYMENT_TRANSACTION_NUMBER',
        visible: true,
        sortable: true,
        frozen: true,
        wrap: true,
        width: '150px',
      },
      buyerAccount: {
        field: 'buyerAccount',
        header: 'ARC_PAYMENT_ACCOUNT',
        visible: true,
        sortable: false,
        wrap: true,
        width: '150px',
      },
      discountApplied: {
        field: 'discountApplied',
        header: 'ARC_PAYMENT_DISCOUNT_APPLIED',
        visible: true,
        sortable: false,
        currency: true,
        width: '150px',
      },
      subtotalAmount: {
        field: 'subtotalAmount',
        header: 'ARC_PAYMENT_SUBTOTAL',
        visible: true,
        sortable: false,
        currency: true,
        width: '150px',
      },
      creditMemoAmount: {
        field: 'creditMemoAmount',
        header: 'ARC_PAYMENT_CREDIT_MEMO_AMOUNT',
        visible: true,
        sortable: false,
        currency: true,
        width: '150px',
      },
      creditCardProcessingFee: {
        field: 'creditCardProcessingFee',
        header: 'ARC_PAYMENT_CC_PROCESSING_FEE',
        visible: true,
        sortable: false,
        currency: true,
        width: '150px',
      },
      paymentAmount: {
        field: 'paymentAmount',
        header: 'ARC_PAYMENT_PAYABLE_AMOUNT',
        visible: true,
        sortable: false,
        frozen: true,
        alignFrozen: 'right',
        currency: true,
        width: '150px',
      },
      status: {
        field: 'status',
        header: 'ARC_PAYMENT_STATUS',
        visible: true,
        sortable: false,
        wrap: false,
        width: '105px',
      },
    };

    this.columns = Object.values(this.columnMap);
  }

  private setVisibleColumns() {
    const columnsToHide: ColumnField[] = [];

    if (this.transactions.every((t) => t.totalDiscount === 0)) {
      columnsToHide.push('discountApplied');
    }

    if (this.transactions.every((t) => t.totalCredit === 0)) {
      columnsToHide.push('creditMemoAmount');
    }

    if (!this.isSurchargeFeeVisible) {
      columnsToHide.push('creditCardProcessingFee');
    }

    this.excludedColumns?.forEach((column) => {
      columnsToHide.push(column);
    });

    columnsToHide.forEach((column) => {
      this.columnMap[column].visible = false;
    });
  }

  private calculateScrollHeight() {
    if (
      window.innerHeight > this.minPageHeight &&
      window.innerHeight - this.minPageHeight > this.rowHeight
    ) {
      const rowsToAdd = Math.floor(
        (window.innerHeight - this.minPageHeight) / this.rowHeight,
      );
      const extendedScrollHeight = this.rowHeight * rowsToAdd;
      this.scrollHeight = `${this.noScrollHeight + extendedScrollHeight}px`;
    }
    this.changeDetector.detectChanges();
  }
}
